import React from "react";
import VoltageGauge from "../../components/VoltageGauge";
import CurrentBar from "../../components/CurrentBar";
import CosfiMiniGauge from "../../components/CosfiMiniGauge";
import {
    ResponsiveContainer,
    ScatterChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Line,
    Scatter,
    PieChart,
    Pie,
    Cell,
    Legend,
} from "recharts";
import { useTranslation } from "react-i18next";
import WeeklyEnergyChart from "../../components/WeeklyEnergyChart";
import WeeklyReactiveEnergyChart from "../../components/WeeklyReactiveEnergyChart";
import AnalyzerRatioGauge from "../../components/AnalyzerRatioGauge";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
// ----------------------------
// REACTIVE ENERGY PIE
// ----------------------------
function ReactiveEnergyPie({ importVal = 0, exportVal = 0 }) {
    const { t } = useTranslation();
    const realImport = importVal || 0;
    const realExport = exportVal || 0;

    const drawImport = realImport > 0 ? realImport : 0.001;
    const drawExport = realExport > 0 ? realExport : 0.001;

    const data = [
        { name: t('dashboard.importReactive'), value: drawImport },
        { name: t('dashboard.exportReactive'), value: drawExport },
    ];

    const COLORS = ["#3B82F6", "#EF4444"];

    return (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
                {t('dashboard.reactiveEnergyDist')}
            </h3>

            <div className="flex items-center justify-center h-72">
                <PieChart width={300} height={300}>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="60%"
                        outerRadius={110}
                        dataKey="value"
                    >
                        {data.map((_, i) => (
                            <Cell key={i} fill={COLORS[i]} />
                        ))}
                    </Pie>

                    <Tooltip
                        formatter={(v, _, item) =>
                            `${item?.payload?.value} kVArh`
                        }
                    />
                    <Legend
                        layout="horizontal"
                        align="center"
                        verticalAlign="bottom"
                        wrapperStyle={{
                            transform: "translateY(15px)",   // 🔥 Legend’ı 25px aşağı kaydırır
                        }}
                    />

                </PieChart>

            </div>
        </div>
    );
}

// ----------------------------
// ACTIVE ENERGY PIE
// ----------------------------
function ActiveEnergyPie({ importVal = 0, exportVal = 0 }) {
    const { t } = useTranslation();
    const realImport = importVal || 0;
    const realExport = exportVal || 0;

    const drawImport = realImport > 0 ? realImport : 0.001;
    const drawExport = realExport > 0 ? realExport : 0.001;

    const data = [
        { name: t('dashboard.importActive'), value: drawImport },
        { name: t('dashboard.exportActive'), value: drawExport },
    ];

    const COLORS = ["#10B981", "#F59E0B"];

    return (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
                {t('dashboard.activeEnergyDist')}
            </h3>

            <div className="flex items-center justify-center h-72">
                <PieChart width={300} height={300}>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="60%"
                        outerRadius={110}
                        dataKey="value"
                    >
                        {data.map((_, i) => (
                            <Cell key={i} fill={COLORS[i]} />
                        ))}
                    </Pie>

                    <Tooltip
                        formatter={(v, _, item) =>
                            `${item?.payload?.value} kWh`
                        }
                    />
                    <Legend
                        layout="horizontal"
                        align="center"
                        verticalAlign="bottom"
                        wrapperStyle={{
                            transform: "translateY(15px)",   // 🔥 Legend’ı 25px aşağı kaydırır
                        }}
                    /></PieChart>

            </div>
        </div>
    );
}

// --------------------------------------------------
// MAIN DASHBOARD
// --------------------------------------------------
export default function AnalyzerDashboard({ data, deviceModel, deviceId, refreshKey }) {
    const { t } = useTranslation();
    if (!data) {
        return (
            <div className="p-6 text-center text-slate-500">
                {t('hierarchy.loading')}
            </div>
        );
    }

    const totalQ =
        (data.reactivePower?.Q1 ?? 0) +
        (data.reactivePower?.Q2 ?? 0) +
        (data.reactivePower?.Q3 ?? 0);

    return (
        <div className="space-y-6">

            {/* VOLTAJ + AKIM */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    {t('dashboard.phaseVoltages')}
                </h3>

                <div className="flex flex-col lg:flex-row gap-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 flex-1">
                        {["VL1", "VL2", "VL3"].map((key) => (
                            <VoltageGauge
                                key={key}
                                label={key}
                                value={data.voltages[key]}
                                vtr={data.ratios?.VTR || 1}
                                isLine={false}
                            />
                        ))}

                        {["VL12", "VL23", "VL31"].map((key) => (
                            <VoltageGauge
                                key={key}
                                label={key}
                                value={data.voltages[key]}
                                vtr={data.ratios?.VTR || 1}
                                isLine={true}
                            />
                        ))}
                    </div>

                    <div className="flex flex-row items-end justify-center gap-2 sm:gap-4 md:gap-8 min-w-[220px]">
                        <CurrentBar label="I1" value={data.currents.I1} max={100} />
                        <CurrentBar label="I2" value={data.currents.I2} max={100} />
                        <CurrentBar label="I3" value={data.currents.I3} max={100} />
                        <CurrentBar
                            label="IΣ"
                            value={
                                (data.currents.I1 ?? 0) +
                                (data.currents.I2 ?? 0) +
                                (data.currents.I3 ?? 0)
                            }
                            max={300}
                        />
                    </div>
                </div>
            </div>

            {/* COSFI */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <CosfiMiniGauge label="Cosφ L1" value={data.cosfi.CosFi1} />
                <CosfiMiniGauge label="Cosφ L2" value={data.cosfi.CosFi2} />
                <CosfiMiniGauge label="Cosφ L3" value={data.cosfi.CosFi3} />
                <CosfiMiniGauge label="Cosφ Σ" value={data.cosfi.CosFiT} />
            </div>


            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <AnalyzerRatioGauge
                    deviceId={deviceId}
                    type="end"
                    title={t('dashboard.totalInductive')}
                />

                <AnalyzerRatioGauge
                    deviceId={deviceId}
                    type="cap"
                    title={t('dashboard.totalCapacitive')}
                />
            </div>


            {/* ENERGY PIE CHARTS (SIDE BY SIDE) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ActiveEnergyPie
                    importVal={(data.activeEnergy?.ImportTotal ?? 0) / 1000}
                    exportVal={(data.activeEnergy?.ExportTotal ?? 0) / 1000}
                />

                <ReactiveEnergyPie
                    importVal={(data.reactiveEnergyFull?.ImportTotal ?? 0) / 1000}
                    exportVal={(data.reactiveEnergyFull?.ExportTotal ?? 0) / 1000}
                />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <WeeklyEnergyChart
                    deviceId={deviceId}
                    API_BASE={API_BASE}
                    refreshKey={refreshKey}
                />

                <WeeklyReactiveEnergyChart
                    deviceId={deviceId}
                    API_BASE={API_BASE}
                    refreshKey={refreshKey}
                />
            </div>

            {/* PQ */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    {t('dashboard.pqDiagram')}
                </h3>

                <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                        <ScatterChart>
                            <CartesianGrid stroke="#E5E7EB" />
                            <XAxis type="number" dataKey="x" name="P" unit="kW" stroke="#6B7280" />
                            <YAxis type="number" dataKey="y" name="Q" unit="kVAr" stroke="#6B7280" />
                            <Tooltip />

                            <Line
                                type="monotone"
                                data={[
                                    { x: 0, y: 0 },
                                    {
                                        x: (data.activePower.PTotalImport ?? 0) / 1000,
                                        y: totalQ / 1000,
                                    },
                                ]}
                                stroke="#0EA5E9"
                                strokeWidth={3}
                                dot={false}
                            />

                            <Scatter
                                data={[
                                    {
                                        x: (data.activePower.PTotalImport ?? 0) / 1000,
                                        y: totalQ / 1000,
                                    },
                                ]}
                                fill="#0EA5E9"
                            />
                        </ScatterChart>
                    </ResponsiveContainer>
                </div>
            </div>


        </div>
    );
}
